import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { confirmStripePayment } from "../../services/TransactionService";
import { isCartCheckout } from "../../utils/functions/stripeConfirmRouting";
import { reportPaymentError } from "../../utils/functions/reportPaymentError";
import { ButtonPrimary } from "../../components/ui/ModalDA";

const FormWrap = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const ErrorMsg = styled.p`
  color: #d32f2f;
  margin: 0;
  text-align: center;
  font-size: 0.875rem;
`;

const PayBtnWrap = styled.div`
  display: flex;
  justify-content: ${({ centered }) => (centered ? "center" : "flex-start")};
`;

function CheckoutForm({ onSuccess, setActive, paymentIntentId, onProcessingStart, onProcessingError, cartMode = false }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;

    // Feedback INSTANTANÉ au clic : on désactive le bouton + on affiche un état
    // d'attente immédiat (sinon délai avant la modal Processing → l'user reclique).
    setSubmitting(true);
    setActive && setActive(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: window.location.origin,
      },
    });

    if (error) {
      setErrorMessage(error.message || t('PaymentFailed'));
      setActive && setActive(false);
      setSubmitting(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === "succeeded") {
      onProcessingStart && onProcessingStart();

      // §46 — Mode PANIER : le PaymentIntent panier (metadata { cartOrderId, ... } sans
      // tileId/type) ne doit PAS passer par le confirm legacy mono-article (qui ferait
      // calculateProductPrice({tileId:null}) → 500). On délègue au parent (onSuccess), qui
      // déclenche POST /cart/confirm-stripe-payment (transfert d'asset + email). Le chemin
      // MONO (cartMode falsy + metadata sans cartOrderId) conserve EXACTEMENT le confirm interne.
      if (isCartCheckout({ cartMode, paymentIntentMetadata: paymentIntent.metadata })) {
        setActive && setActive(false);
        onSuccess && onSuccess(paymentIntent);
        return;
      }

      try {
        const res = await confirmStripePayment({ paymentIntentId: paymentIntentId || paymentIntent.id });
        if (res && res.success) {
          setActive && setActive(false);
          onSuccess && onSuccess(res);
        } else {
          setActive && setActive(false);
          const m = t('PaymentSucceededConfirmationFailed');
          // La modal Stripe est déjà fermée (onProcessingStart) → on remonte l'erreur au parent.
          if (onProcessingError) onProcessingError(m); else setErrorMessage(m);
        }
      } catch (err) {
        // §107 — argent encaissé (paymentIntent.status === 'succeeded') mais transfert/confirm
        // KO → CAS LE PLUS CRITIQUE : on rend l'incident visible (UX inchangée).
        reportPaymentError(err, { provider: 'stripe', flow: 'stripe_confirm', moneyTaken: true });
        const msg = (err && err.message) ? err.message : t('ConfirmationFailedContactSupport');
        setActive && setActive(false);
        const m = t('PaymentSucceededTransferFailed') + ' (' + msg.substring(0, 80) + ')';
        if (onProcessingError) onProcessingError(m); else setErrorMessage(m);
      }
    } else {
      setErrorMessage('Unexpected payment status: ' + (paymentIntent ? paymentIntent.status : 'unknown'));
      setActive && setActive(false);
      setSubmitting(false);
    }
  };

  return (
    <FormWrap onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMessage && <ErrorMsg>{errorMessage}</ErrorMsg>}
      {submitting && <ErrorMsg style={{ color: '#0d0a10' }}>{t('ProcessingPaymentPleaseWait')}</ErrorMsg>}
      <PayBtnWrap centered>
        <ButtonPrimary type="submit" disabled={!stripe || submitting}>
          {submitting ? t('Processing') : t('Pay')}
        </ButtonPrimary>
      </PayBtnWrap>
    </FormWrap>
  );
}

export default CheckoutForm;
